import {
  ApartmentOutlined, CrownOutlined, StarFilled, TrophyOutlined, UserOutlined
} from '@ant-design/icons';
import { Avatar, Card, Col, Row, Segmented, Skeleton, Space, Tag, Typography } from 'antd';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaLeaderboardItem, AreaPodium, PodiumCategory, SalesLeaderboardItem, SalesPodium
} from '../../../../types/leaderboard';
import { formatNumber, formatRupiah, glassCardStyle } from '../../utils';

const { Title, Text } = Typography;

interface PodiumShowcaseProps {
  type: 'sales' | 'area';
  salesPodium?: SalesPodium;
  areaPodium?: AreaPodium;
  loading: boolean;
}

export const PodiumShowcase: React.FC<PodiumShowcaseProps> = ({
  type,
  salesPodium,
  areaPodium,
  loading,
}) => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<PodiumCategory>('top_rsv');

  // Active items based on selected category, strictly filtered by threshold > 0 (Point 6.A)
  const salesItems: SalesLeaderboardItem[] = (salesPodium ? salesPodium[activeCategory] || [] : []).filter(
    (item) => {
      if (activeCategory === 'top_omzet') return Number(item.omzet || 0) > 0;
      if (activeCategory === 'top_ok') return Number(item.ok_count || 0) > 0;
      if (activeCategory === 'top_rsv') return Number(item.rsv_count || 0) > 0;
      return false;
    }
  );

  const areaItems: AreaLeaderboardItem[] = (areaPodium ? areaPodium[activeCategory] || [] : []).filter(
    (item) => {
      if (activeCategory === 'top_omzet') return Number(item.omzet || 0) > 0;
      if (activeCategory === 'top_ok') return Number(item.ok_count || 0) > 0;
      if (activeCategory === 'top_rsv') return Number(item.rsv_count || 0) > 0;
      return false;
    }
  );

  const rankBadgeConfig = {
    1: {
      bg: '#faad14',
      border: 'rgba(250, 173, 20, 0.45)',
      elevation: 'translateY(-8px)',
    },
    2: {
      bg: '#94a3b8',
      border: 'rgba(203, 213, 225, 0.6)',
      elevation: 'none',
    },
    3: {
      bg: '#d97706',
      border: 'rgba(217, 119, 6, 0.35)',
      elevation: 'none',
    },
  };

  const getCategoryMeta = () => {
    switch (activeCategory) {
      case 'top_rsv':
        return {
          label: 'Top Reservasi (RSV)',
          unit: 'Lead Reservasi (RSV)',
          description: 'Peringkat 3 teratas dengan perolehan lead booking/reservasi (RSV) terbanyak',
        };
      case 'top_omzet':
        return {
          label: 'Top Omzet',
          unit: 'Realisasi Omzet',
          description: 'Peringkat 3 teratas dengan nilai transaksi closing yang disetujui (Approved)',
        };
      case 'top_ok':
        return {
          label: 'Top Closing (O K)',
          unit: 'Lead Closing (O K)',
          description: 'Peringkat 3 teratas dengan jumlah transaksi closing (O K) terbanyak',
        };
    }
  };

  const categoryMeta = getCategoryMeta();

  // Render Sales Podium Card
  const renderSalesCard = (item: SalesLeaderboardItem, rankPosition: 1 | 2 | 3) => {
    const config = rankBadgeConfig[rankPosition];
    const isFirst = rankPosition === 1;

    return (
      <div
        key={item.user_id}
        onClick={() => navigate(`/performa/sales-detail/${item.user_id}`)}
        style={{
          ...glassCardStyle,
          cursor: 'pointer',
          padding: isFirst ? '24px 20px' : '20px 18px',
          border: `1px solid ${config.border}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          transition: 'all 0.25s ease',
          position: 'relative',
          transform: config.elevation,
          minHeight: '310px',
        }}
        className="podium-card-interactive"
      >
        {/* Subtle Rank Badge */}
        <div
          style={{
            position: 'absolute',
            top: '-14px',
            background: config.bg,
            color: '#fff',
            width: isFirst ? '32px' : '26px',
            height: isFirst ? '32px' : '26px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: isFirst ? '14px' : '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            border: '2px solid #fff',
            zIndex: 2,
          }}
        >
          {isFirst ? <CrownOutlined /> : rankPosition}
        </div>

        {/* Sales Avatar */}
        <div style={{ marginTop: isFirst ? '6px' : '2px', marginBottom: '12px' }}>
          <Avatar
            size={isFirst ? 76 : 62}
            src={item.avatar}
            icon={<UserOutlined />}
            style={{
              backgroundColor: '#1890ff',
              border: isFirst ? '2px solid #faad14' : '2px solid rgba(255,255,255,0.8)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            }}
          />
        </div>

        {/* Sales Name */}
        <Title
          level={5}
          style={{
            margin: '0 0 4px',
            color: '#0f172a',
            fontWeight: 700,
            lineHeight: 1.3,
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={item.user_name}
        >
          {item.user_name}
        </Title>

        {/* Property Areas Tag */}
        <div style={{ marginBottom: '14px', minHeight: '22px' }}>
          {Array.isArray(item.property_areas) && item.property_areas.length > 0 ? (
            <Tag style={{ borderRadius: '4px', fontSize: '11px', margin: '2px', border: '1px solid #e2e8f0', background: 'rgba(255,255,255,0.7)' }}>
              {item.property_areas[0]}
              {item.property_areas.length > 1 && ` +${item.property_areas.length - 1}`}
            </Tag>
          ) : (
            <Tag style={{ borderRadius: '4px', fontSize: '11px', margin: '2px' }}>Agen Sales</Tag>
          )}
        </div>

        {/* Primary Metric Box (Harmonized with SummaryCards style) */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.65)',
            border: '1px solid rgba(226, 232, 240, 0.9)',
            borderRadius: '10px',
            padding: '10px 14px',
            width: '100%',
            marginBottom: '12px',
          }}
        >
          <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
            {categoryMeta.unit}
          </Text>
          <Title level={4} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
            {activeCategory === 'top_omzet' ? (
              item.omzet_rupiah || formatRupiah(item.omzet)
            ) : activeCategory === 'top_ok' ? (
              `${formatNumber(item.ok_count)}`
            ) : (
              `${formatNumber(item.rsv_count)}`
            )}
          </Title>
        </div>

        {/* Supporting Meta Stats */}
        <div style={{ width: '100%', marginTop: 'auto', paddingTop: '8px', borderTop: '1px solid rgba(226, 232, 240, 0.6)' }}>
          <Row gutter={8} justify="space-around">
            <Col span={8}>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Closing</Text>
              <Text strong style={{ fontSize: '12px', color: '#1e293b' }}>{formatNumber(item.ok_count)}</Text>
            </Col>
            <Col span={8}>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>RSV</Text>
              <Text strong style={{ fontSize: '12px', color: '#1e293b' }}>{formatNumber(item.rsv_count)}</Text>
            </Col>
            <Col span={8}>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Konversi</Text>
              <Text strong style={{ fontSize: '12px', color: '#1e293b' }}>{item.conversion_rate?.toFixed(1)}%</Text>
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  // Render Area Podium Card
  const renderAreaCard = (item: AreaLeaderboardItem, rankPosition: 1 | 2 | 3) => {
    const config = rankBadgeConfig[rankPosition];
    const isFirst = rankPosition === 1;

    return (
      <div
        key={item.property_area_id}
        style={{
          ...glassCardStyle,
          padding: isFirst ? '24px 20px' : '20px 18px',
          border: `1px solid ${config.border}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          transition: 'all 0.25s ease',
          position: 'relative',
          transform: config.elevation,
          minHeight: '310px',
        }}
        className="podium-card-interactive"
      >
        {/* Subtle Rank Badge */}
        <div
          style={{
            position: 'absolute',
            top: '-14px',
            background: config.bg,
            color: '#fff',
            width: isFirst ? '32px' : '26px',
            height: isFirst ? '32px' : '26px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: isFirst ? '14px' : '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            border: '2px solid #fff',
            zIndex: 2,
          }}
        >
          {isFirst ? <CrownOutlined /> : rankPosition}
        </div>

        {/* Area Avatar / Icon */}
        <div style={{ marginTop: isFirst ? '6px' : '2px', marginBottom: '12px' }}>
          <Avatar
            size={isFirst ? 76 : 62}
            icon={<ApartmentOutlined />}
            style={{
              backgroundColor: '#1890ff',
              border: isFirst ? '2px solid #faad14' : '2px solid rgba(255,255,255,0.8)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            }}
          />
        </div>

        {/* Area Name */}
        <Title
          level={5}
          style={{
            margin: '0 0 4px',
            color: '#0f172a',
            fontWeight: 700,
            lineHeight: 1.3,
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={item.property_area_name}
        >
          {item.property_area_name}
        </Title>

        {/* Area Status Tag */}
        <div style={{ marginBottom: '14px', minHeight: '22px' }}>
          {item.is_my_area ? (
            <Tag color="blue" icon={<StarFilled style={{ fontSize: '10px' }} />} style={{ borderRadius: '4px', fontSize: '11px' }}>
              Kawasan Saya
            </Tag>
          ) : (
            <Tag style={{ borderRadius: '4px', fontSize: '11px', border: '1px solid #e2e8f0', background: 'rgba(255,255,255,0.7)' }}>
              {item.total_sales || 0} Agen Sales
            </Tag>
          )}
        </div>

        {/* Primary Metric Box */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.65)',
            border: '1px solid rgba(226, 232, 240, 0.9)',
            borderRadius: '10px',
            padding: '10px 14px',
            width: '100%',
            marginBottom: '12px',
          }}
        >
          <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
            {categoryMeta.unit}
          </Text>
          <Title level={4} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
            {activeCategory === 'top_omzet' ? (
              item.omzet_rupiah || formatRupiah(item.omzet)
            ) : activeCategory === 'top_ok' ? (
              `${formatNumber(item.ok_count)} Unit`
            ) : (
              `${formatNumber(item.rsv_count)}`
            )}
          </Title>
        </div>

        {/* Supporting Meta Stats */}
        <div style={{ width: '100%', marginTop: 'auto', paddingTop: '8px', borderTop: '1px solid rgba(226, 232, 240, 0.6)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>Top Sales:</Text>
            <Text strong style={{ fontSize: '11px', color: '#1e293b' }}>
              {item.top_sales_name || '-'}
            </Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>Total Leads:</Text>
            <Text strong style={{ fontSize: '11px', color: '#1e293b' }}>
              {formatNumber(item.total_leads || 0)}
            </Text>
          </div>
        </div>
      </div>
    );
  };

  // Build the Olympic 2-1-3 layout: Left is #2, Center is #1, Right is #3
  const renderPodiumLayout = () => {
    const rawList = type === 'sales' ? salesItems : areaItems;

    if (loading) {
      return (
        <Row gutter={[20, 20]} justify="center" align="bottom" style={{ minHeight: '320px' }}>
          <Col xs={24} md={8}>
            <Card style={{ ...glassCardStyle, borderRadius: '14px' }}>
              <Skeleton active avatar paragraph={{ rows: 4 }} />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ ...glassCardStyle, borderRadius: '14px', minHeight: '340px' }}>
              <Skeleton active avatar paragraph={{ rows: 5 }} />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ ...glassCardStyle, borderRadius: '14px' }}>
              <Skeleton active avatar paragraph={{ rows: 4 }} />
            </Card>
          </Col>
        </Row>
      );
    }

    if (!rawList || rawList.length === 0) {
      return (
        <div
          style={{
            ...glassCardStyle,
            textAlign: 'center',
            padding: '48px 24px',
            borderRadius: '14px',
            color: '#64748b',
          }}
        >
          <TrophyOutlined style={{ fontSize: '38px', color: '#cbd5e1', marginBottom: '12px' }} />
          <Title level={4} style={{ color: '#475569', margin: '0 0 6px' }}>
            Belum Ada Prestasi di Kategori Ini
          </Title>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            Belum ada transaksi di periode ini. Semangat bekerja! 🚀
          </Text>
        </div>
      );
    }

    // Identify #1, #2, #3 items safely with strict typing
    if (type === 'sales') {
      const firstItem = salesItems.find((it: SalesLeaderboardItem) => it.rank === 1) || salesItems[0];
      const secondItem = salesItems.find((it: SalesLeaderboardItem) => it.rank === 2) || (salesItems.length > 1 ? salesItems[1] : null);
      const thirdItem = salesItems.find((it: SalesLeaderboardItem) => it.rank === 3) || (salesItems.length > 2 ? salesItems[2] : null);

      return (
        <Row gutter={[20, 20]} justify="center" align="bottom" style={{ marginTop: '16px' }}>
          {/* Rank 2 (Left on Desktop) */}
          <Col xs={24} sm={24} md={8} style={{ order: 2 }}>
            {secondItem ? (
              renderSalesCard(secondItem, 2)
            ) : (
              <div style={{ ...glassCardStyle, minHeight: '260px', opacity: 0.65, border: '1px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>Peringkat #2 Belum Terisi</Text>
              </div>
            )}
          </Col>

          {/* Rank 1 (Center on Desktop) */}
          <Col xs={24} sm={24} md={8} style={{ order: 1 }}>
            {firstItem ? renderSalesCard(firstItem, 1) : null}
          </Col>

          {/* Rank 3 (Right on Desktop) */}
          <Col xs={24} sm={24} md={8} style={{ order: 3 }}>
            {thirdItem ? (
              renderSalesCard(thirdItem, 3)
            ) : (
              <div style={{ ...glassCardStyle, minHeight: '240px', opacity: 0.65, border: '1px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>Peringkat #3 Belum Terisi</Text>
              </div>
            )}
          </Col>
        </Row>
      );
    }

    // Area branch
    const firstArea = areaItems.find((it: AreaLeaderboardItem) => it.rank === 1) || areaItems[0];
    const secondArea = areaItems.find((it: AreaLeaderboardItem) => it.rank === 2) || (areaItems.length > 1 ? areaItems[1] : null);
    const thirdArea = areaItems.find((it: AreaLeaderboardItem) => it.rank === 3) || (areaItems.length > 2 ? areaItems[2] : null);

    return (
      <Row gutter={[20, 20]} justify="center" align="bottom" style={{ marginTop: '16px' }}>
        {/* Rank 2 (Left on Desktop) */}
        <Col xs={24} sm={24} md={8} style={{ order: 2 }}>
          {secondArea ? (
            renderAreaCard(secondArea, 2)
          ) : (
            <div style={{ ...glassCardStyle, minHeight: '260px', opacity: 0.65, border: '1px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <Text type="secondary" style={{ fontSize: '13px' }}>Peringkat #2 Belum Terisi</Text>
            </div>
          )}
        </Col>

        {/* Rank 1 (Center on Desktop) */}
        <Col xs={24} sm={24} md={8} style={{ order: 1 }}>
          {firstArea ? renderAreaCard(firstArea, 1) : null}
        </Col>

        {/* Rank 3 (Right on Desktop) */}
        <Col xs={24} sm={24} md={8} style={{ order: 3 }}>
          {thirdArea ? (
            renderAreaCard(thirdArea, 3)
          ) : (
            <div style={{ ...glassCardStyle, minHeight: '240px', opacity: 0.65, border: '1px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <Text type="secondary" style={{ fontSize: '13px' }}>Peringkat #3 Belum Terisi</Text>
            </div>
          )}
        </Col>
      </Row>
    );
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <style>{`
        .podium-card-interactive:hover {
          transform: translateY(-6px) !important;
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.06) !important;
        }
      `}</style>

      {/* Header & Category Switcher */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '16px',
        }}
      >
        <Space size={8}>
          <TrophyOutlined style={{ color: '#faad14', fontSize: '18px' }} />
          <div>
            <Title level={4} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
              Top 3 {type === 'sales' ? 'Sales Agent' : 'Kawasan'}
            </Title>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {categoryMeta.description}
            </Text>
          </div>
        </Space>

        {/* Category Switcher */}
        <Segmented
          value={activeCategory}
          onChange={(val) => setActiveCategory(val as PodiumCategory)}
          options={[
            { label: 'Top Reservasi (RSV)', value: 'top_rsv' },
            { label: 'Top Closing (OK)', value: 'top_ok' },
            { label: 'Top Omzet', value: 'top_omzet' },

          ]}
        />
      </div>

      {/* Render Podium Cards */}
      {renderPodiumLayout()}
    </div>
  );
};

export default PodiumShowcase;
